import type { IndicatorDoc } from './schema';

export const INDICATOR_MARKET_FEATURES = [
  'area',
  'baseline',
  'histogram',
  'hline:label',
  'line:opacity',
  'line:style',
  'marker:square',
  'op:abs',
  'op:ffill',
  'op:max2',
  'op:min2',
  'pane',
  'slot:soft',
  'source:investor_flow',
  'source:sector_flow',
  'source:symbol',
] as const;

export type IndicatorMarketFeature = (typeof INDICATOR_MARKET_FEATURES)[number];

/** 앱 버전별 누적 지원 집합. note는 표시 전용이라 호환성 feature에 넣지 않는다. */
export const SUPPORTED_INDICATOR_FEATURES_BY_APP_VERSION = {
  '1.0': [] as const,
  '1.5': INDICATOR_MARKET_FEATURES,
} as const;

export const CURRENT_SUPPORTED_INDICATOR_FEATURES: readonly IndicatorMarketFeature[] =
  SUPPORTED_INDICATOR_FEATURES_BY_APP_VERSION['1.5'];

export const SOURCE_FEATURE_LABELS: Partial<Record<IndicatorMarketFeature, string>> = {
  'source:symbol': '다른 종목 데이터 필요',
  'source:sector_flow': '섹터 데이터 필요',
  'source:investor_flow': '수급 데이터 필요',
};

export function sourceFeatureLabels(features: readonly string[]): string[] {
  return features.flatMap((feature) => {
    const label = SOURCE_FEATURE_LABELS[feature as IndicatorMarketFeature];
    return label ? [label] : [];
  });
}

export function supportsIndicatorFeatures(features: readonly string[], supportedFeatures: readonly string[]): boolean {
  const supported = new Set(supportedFeatures);
  return features.every((feature) => supported.has(feature));
}

/** note를 재귀 제거한 뒤 모든 객체 키를 정렬한다. 배열 순서는 DSL 실행 의미라 보존한다. */
export function canonicalizeIndicatorDocForHash(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalizeIndicatorDocForHash);
  if (!value || typeof value !== 'object') return value;
  const source = value as Record<string, unknown>;
  return Object.fromEntries(
    Object.keys(source)
      .filter((key) => key !== 'note')
      .sort()
      .map((key) => [key, canonicalizeIndicatorDocForHash(source[key])]),
  );
}

export function canonicalIndicatorDocJson(doc: IndicatorDoc): string {
  return JSON.stringify(canonicalizeIndicatorDocForHash(doc));
}
